import { IDestroyOptions, Rectangle } from "pixi.js";
import { ICanvasScaler } from "../../../../../../canvas/ICanvasScaler";
import { PromiseCompletionSource } from "../../../../../../shared/async/PromiseCompletionSource";
import { ButtonWithScaleAnimation } from "../../../../common/components/ButtonWithScaleAnimation";
import { CommonResourcesPackage } from "../../../../common/resources/CommonResourcesPackage";
import { FadeScreen } from "../../../../game/components/FadeScreen";
import { LoginResourcePackage } from "../../resources/LoginResourcePackage";
import { gsap } from "gsap";
import { SignInSuccessfulContainer } from "../components/SignInSuccessfulContainer";
import { IGuestModel } from "by/gamefactory/cityversetycoon/scenes/common/models/IGuestModel";
import { IFocusable } from "by/gamefactory/cityversetycoon/scenes/layers/IFocusable";
import { BaseScalableView } from "by/gamefactory/cityversetycoon/scenes/common/BaseScalableView";
import { UserProfileResourcePackage } from "../../../userprofile/resources/UserProfileResourcePackage";

export class SignInSuccessfulPopupView extends BaseScalableView<boolean> implements IFocusable {
    public readonly focusable: boolean = true;

    private readonly loginResources: LoginResourcePackage;
    private readonly commonResources: CommonResourcesPackage;
    private readonly userProfileResources: UserProfileResourcePackage;
    private readonly guestModel: IGuestModel;
    private fadeScreen: FadeScreen;
    private frameContainer: SignInSuccessfulContainer;
    private closeButton: ButtonWithScaleAnimation;

    public constructor(
        operationControls: PromiseCompletionSource<boolean>,
        loginResources: LoginResourcePackage,
        commonResources: CommonResourcesPackage,
        userProfileResources: UserProfileResourcePackage,
        canvasScaler: ICanvasScaler,
        guestModel: IGuestModel
    ) {
        super(operationControls, canvasScaler);
        this.loginResources = loginResources;
        this.commonResources = commonResources;
        this.userProfileResources = userProfileResources;
        this.guestModel = guestModel;
        this.build();
        this.layout(this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
    }

    public start(): void {
        this.show();
    }

    private build(): void {
        const width = this.canvasScaler.referenceWidth;
        const height = this.canvasScaler.referenceHeight;
        this.fadeScreen = new FadeScreen(0.1, 0, 0.8, width, height);
        this.fadeScreen.eventMode = "static";
        this.addChild(this.fadeScreen);

        this.frameContainer = new SignInSuccessfulContainer(this.loginResources, this.userProfileResources, this.guestModel);
        this.frameContainer.onOk.add(this.okButtonClickHandle, this);
        this.addChild(this.frameContainer);

        this.closeButton = new ButtonWithScaleAnimation(this.commonResources.assets_common_x_png);
        this.closeButton.hitArea = new Rectangle(-this.closeButton.width * 0.5, -this.closeButton.height, this.closeButton.width * 2, this.closeButton.height * 2);
        this.closeButton.onUp.add(this.closeButtonClickHandle, this);
        this.addChild(this.closeButton);
    }

    private async show(): Promise<void> {
        this.fadeScreen.fadeIn();
        const showAnimation = new gsap.core.Timeline();
        showAnimation.fromTo(this, { alpha: 0 }, { alpha: 1, duration: 0.15, ease: "power1.in" });
        await showAnimation.play();
        showAnimation.kill();
    }

    private async closeButtonClickHandle(): Promise<void> {
        this.closeButton.eventMode = "auto";
        await this.hideView();
        this.operationControls.complete(false);
    }

    private async okButtonClickHandle(): Promise<void> {
        await this.hideView();
        this.operationControls.complete(true);
    }

    private async hideView(): Promise<void> {
        const hideAnimation = new gsap.core.Timeline();
        hideAnimation.to(this, { alpha: 0, duration: 0.15, ease: "power1.out" });
        await hideAnimation.play();
        hideAnimation.kill();
    }

    private layout(width: number, height: number): void {
        this.fadeScreen.width = width;
        this.fadeScreen.height = height;
        this.frameContainer.position.set(width * 0.5, height * 0.284);
        this.closeButton.position.set(374, 1347);
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.closeButton.onUp.remove(this.closeButtonClickHandle, this);
        this.frameContainer.onOk.removeAll();
        super.destroy(options);
    }
}
